// siteApi.js - API utilities for site management operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log(
        'Successfully retrieved valid ID token for site management API'
      );
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Get All Sites API
 * Fetches all sites for the current user's tenant
 * Endpoint: /site/getall (matches Lambda function: rakshak-getall-sites)
 */
export const getAllSites = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/getall`);

    const response = await fetch(`${API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/getall';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for getAllSites`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Sites fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching sites:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for getAllSites`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Site Details API
 * Fetches a specific site by its ID
 * Endpoint: /site (GET method) (matches Lambda function: rakshak-get-site-details)
 */
export const getSiteDetails = async siteId => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site?site_id=${siteId}`);

    const response = await fetch(`${API_BASE_URL}/site?site_id=${siteId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for getSiteDetails`,
        error,
        {
          endpoint: '/site',
          method: 'GET',
          siteId,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Site details fetched successfully:', result);
    return normalizeSiteRecord(result);
  } catch (error) {
    console.error('Error fetching site details:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for getSiteDetails`,
        error,
        {
          endpoint: '/site',
          method: 'GET',
          siteId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Create Site API
 * Creates a new site with geofencing
 * Endpoint: /site/create (POST method) (matches Lambda function: rakshak-post-geofencing)
 */
export const createSite = async siteData => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/create`);
    console.log('Request Body:', JSON.stringify(siteData, null, 2));

    const response = await fetch(`${API_BASE_URL}/site/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(siteData),
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error('API Error Response:', responseText);
      
      const error = new Error(`HTTP ${response.status}: ${responseText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/create';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for createSite`,
        error,
        {
          endpoint: '/site/create',
          method: 'POST',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Site created successfully:', result);
    return result;
  } catch (error) {
    console.error('Error creating site:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for createSite`,
        error,
        {
          endpoint: '/site/create',
          method: 'POST',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Update Site API
 * Updates an existing site
 * Endpoint: /site (PUT method) (matches Lambda function: rakshak-put-geofencing)
 */
export const updateSite = async (siteId, siteData) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    const requestBody = { site_id: siteId, ...siteData };
    console.log(`Calling endpoint: ${API_BASE_URL}/site`);
    console.log('Request Body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${API_BASE_URL}/site`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error('API Error Response:', responseText);
      
      const error = new Error(`HTTP ${response.status}: ${responseText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for updateSite`,
        error,
        {
          endpoint: '/site',
          method: 'PUT',
          siteId,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Site updated successfully:', result);
    return result;
  } catch (error) {
    console.error('Error updating site:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for updateSite`,
        error,
        {
          endpoint: '/site',
          method: 'PUT',
          siteId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Delete Site API
 * Deletes a site record and all associated records
 * Endpoint: /site/remove (DELETE method) (matches Lambda function: rakshak-delete-site-records)
 */
export const deleteSite = async siteId => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/remove`);

    const response = await fetch(`${API_BASE_URL}/site/remove`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ site_id: siteId }),
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error('API Error Response:', responseText);
      
      const error = new Error(`HTTP ${response.status}: ${responseText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/remove';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for deleteSite`,
        error,
        {
          endpoint: '/site/remove',
          method: 'DELETE',
          siteId,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Site deleted successfully:', result);
    return result;
  } catch (error) {
    console.error('Error deleting site:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for deleteSite`,
        error,
        {
          endpoint: '/site/remove',
          method: 'DELETE',
          siteId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Update Geofence API
 * Updates site fields including geofence details
 * Endpoint: /site/geofence/update (PUT method)
 * Accepts: site_id, geofence_details, site_name, address, authorised_ids, bluetooth_enabled
 */
export const presignSiteLogoUpload = async (
  siteId,
  contentType = 'image/jpeg'
) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    const endpoint = `${API_BASE_URL}/site/${siteId}/logo/presign?content_type=${encodeURIComponent(contentType)}`;
    console.log(`Calling endpoint: ${endpoint}`);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      throw new Error(`HTTP ${response.status}: ${responseText}`);
    }

    const result = await response.json();
    return {
      success: true,
      presigned_post: result.presigned_post,
      object_key: result.object_key,
      content_type: result.content_type || contentType,
    };
  } catch (error) {
    console.error('Error getting site logo presign URL:', error);
    return {
      success: false,
      message: error.message || 'Failed to get presigned URL for site logo',
    };
  }
};

/**
 * Upload a site logo:
 * 1. POST /site/{site_id}/logo/presign
 * 2. Upload file to S3 via presigned_post
 * 3. PUT /site with logo_key and logo_content_type
 */
export const uploadSiteLogo = async (siteId, logoPreview, logoData) => {
  const contentType =
    logoPreview?.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
  const presignResult = await presignSiteLogoUpload(siteId, contentType);

  if (!presignResult.success) {
    throw new Error(presignResult.message || 'Failed to prepare logo upload');
  }

  const base64Data = logoPreview?.includes(',')
    ? logoPreview.split(',')[1]
    : logoData;
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });

  const s3FormData = new FormData();
  Object.entries(presignResult.presigned_post.fields).forEach(([key, value]) => {
    s3FormData.append(key, value);
  });
  s3FormData.append('file', blob);

  const uploadResponse = await fetch(presignResult.presigned_post.url, {
    method: 'POST',
    body: s3FormData,
  });

  if (!uploadResponse.ok) {
    const uploadErrorText = await uploadResponse.text();
    throw new Error(
      `Logo upload failed (${uploadResponse.status}): ${uploadErrorText}`
    );
  }

  return updateSite(siteId, {
    logo_key: presignResult.object_key,
    logo_content_type: contentType,
  });
};

const normalizeSiteRecord = siteRecord => {
  if (!siteRecord || typeof siteRecord !== 'object') {
    return siteRecord;
  }

  if (siteRecord.site && typeof siteRecord.site === 'object') {
    return { ...siteRecord, ...siteRecord.site };
  }

  return siteRecord;
};

const parseLogoUrlFromResponse = data => {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    return data.startsWith('http') || data.startsWith('data:') ? data : null;
  }

  if (typeof data !== 'object') {
    return null;
  }

  return (
    getSiteLogoUrl(data) ||
    data.url ||
    data.presigned_url ||
    data.presigned_get_url ||
    data.logo_presigned_get_url ||
    data.download_url ||
    data.signed_url ||
    null
  );
};

const fetchImageAsBlobIfNeeded = async url => {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return null;
  }

  if (!url.includes('api.innoworx.ai')) {
    return null;
  }

  try {
    const token = await getAuthToken();
    if (!token) {
      return null;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.startsWith('image/')) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.warn('[siteApi] Failed to fetch logo image as blob:', error);
    return null;
  }
};

const fetchLogoFromEndpoint = async (endpoint, token) => {
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const result = await response.json();
    const parsedUrl = parseLogoUrlFromResponse(result);
    if (!parsedUrl) {
      return null;
    }

    const blobUrl = await fetchImageAsBlobIfNeeded(parsedUrl);
    return blobUrl || parsedUrl;
  }

  if (contentType.startsWith('image/')) {
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  return null;
};

export const getSiteLogoUrl = site => {
  const normalizedSite = normalizeSiteRecord(site);
  if (!normalizedSite || typeof normalizedSite !== 'object') {
    return null;
  }

  const directUrl =
    normalizedSite.logo_presigned_url ||
    normalizedSite.logo_url ||
    normalizedSite.logoUrl ||
    normalizedSite.site_logo_url ||
    normalizedSite.presigned_logo_url ||
    normalizedSite.logo_signed_url ||
    null;

  if (directUrl) {
    return directUrl;
  }

  const nestedLogo = normalizedSite.logo;
  if (nestedLogo && typeof nestedLogo === 'object') {
    return (
      nestedLogo.presigned_url ||
      nestedLogo.logo_presigned_url ||
      nestedLogo.url ||
      nestedLogo.logo_url ||
      null
    );
  }

  return null;
};

export const siteHasLogo = site => {
  const normalizedSite = normalizeSiteRecord(site);
  if (!normalizedSite) {
    return false;
  }

  return Boolean(
    normalizedSite.logo_key ||
    normalizedSite.logo_url ||
    normalizedSite.logo_presigned_url ||
    normalizedSite.logoUrl ||
    getSiteLogoUrl(normalizedSite)
  );
};

/**
 * Fetch a fresh viewable logo URL for display.
 * Tries GET /site/{site_id}/logo and GET /site/{site_id}/logo/presign.
 */
export const fetchSiteLogoViewUrl = async siteId => {
  try {
    const token = await getAuthToken();
    if (!token) {
      return null;
    }

    const endpoints = [
      `${API_BASE_URL}/site/${siteId}/logo`,
      `${API_BASE_URL}/site/${siteId}/logo/presign`,
    ];

    for (const endpoint of endpoints) {
      const logoUrl = await fetchLogoFromEndpoint(endpoint, token);
      if (logoUrl) {
        return logoUrl;
      }
    }

    return null;
  } catch (error) {
    console.warn('[siteApi] Failed to fetch site logo view URL:', error);
    return null;
  }
};

/**
 * Resolve a logo URL safe for <img src> — prefers a fresh API URL over stale fields.
 */
export const resolveSiteLogoUrl = async site => {
  const normalizedSite = normalizeSiteRecord(site);
  const siteId = normalizedSite?.site_id;
  if (!siteId) {
    return null;
  }

  const logoFromApi = await fetchSiteLogoViewUrl(siteId);
  if (logoFromApi) {
    return logoFromApi;
  }

  const directUrl = getSiteLogoUrl(normalizedSite);
  if (directUrl) {
    const blobUrl = await fetchImageAsBlobIfNeeded(directUrl);
    return blobUrl || directUrl;
  }

  if (normalizedSite.logo_key || siteHasLogo(normalizedSite)) {
    try {
      const refreshedSite = normalizeSiteRecord(await getSiteDetails(siteId));
      const refreshedUrl = getSiteLogoUrl(refreshedSite);
      if (refreshedUrl) {
        const blobUrl = await fetchImageAsBlobIfNeeded(refreshedUrl);
        return blobUrl || refreshedUrl;
      }
    } catch (error) {
      console.warn('[siteApi] Failed to refresh site for logo URL:', error);
    }
  }

  return null;
};

export const updateGeofence = async (siteId, updateData) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    // Build request body with site_id and any provided fields
    const requestBody = {
      site_id: siteId,
      ...updateData, // Can include: geofence_details, site_name, address, authorised_ids, bluetooth_enabled
    };

    console.log(`Calling endpoint: ${API_BASE_URL}/site/geofence/update`);
    console.log('Request Body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${API_BASE_URL}/site/geofence/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error('API Error Response:', responseText);
      
      const error = new Error(`HTTP ${response.status}: ${responseText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/geofence/update';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for updateGeofence`,
        error,
        {
          endpoint: '/site/geofence/update',
          method: 'PUT',
          siteId,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Site updated successfully:', result);
    return result;
  } catch (error) {
    console.error('Error updating site:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for updateGeofence`,
        error,
        {
          endpoint: '/site/geofence/update',
          method: 'PUT',
          siteId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Sites for Current User API
 * Fetches sites assigned to the current user
 * Endpoint: /site/get-sites-for-user (matches Flutter implementation)
 */
export const getSitesForCurrentUser = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/get-sites-for-user`);

    const response = await fetch(`${API_BASE_URL}/site/get-sites-for-user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/site/get-sites-for-user';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for getSitesForCurrentUser`,
        error,
        {
          endpoint: '/site/get-sites-for-user',
          method: 'GET',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('User sites fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching user sites:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for getSitesForCurrentUser`,
        error,
        {
          endpoint: '/site/get-sites-for-user',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Validate Site Data
 * Validates site data before sending to API
 */
export const validateSiteData = siteData => {
  const errors = [];
  const requiredFields = ['site_name', 'address', 'geofence_details'];

  requiredFields.forEach(field => {
    if (!siteData[field] || siteData[field].toString().trim() === '') {
      errors.push(
        `${field
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()} is required`
      );
    }
  });

  // Validate geofence details
  if (siteData.geofence_details) {
    const { type, coordinates, radius, center } = siteData.geofence_details;

    if (type === 'Polygon') {
      if (
        !coordinates ||
        !Array.isArray(coordinates) ||
        coordinates.length < 3
      ) {
        errors.push('Polygon must have at least 3 coordinate points');
      }
    } else if (type === 'Circle') {
      if (!radius || radius <= 0) {
        errors.push('Circle radius must be greater than 0');
      }
      if (!center || !Array.isArray(center) || center.length !== 2) {
        errors.push('Circle must have a valid center point [lat, lng]');
      }
    } else {
      errors.push('Geofence type must be either "Polygon" or "Circle"');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Parse geofence details from string (for display purposes)
 */
export const parseGeofenceDetails = geofenceDetailsString => {
  try {
    if (typeof geofenceDetailsString === 'string') {
      return JSON.parse(geofenceDetailsString);
    }
    return geofenceDetailsString;
  } catch (error) {
    console.error('Error parsing geofence details:', error);
    return { type: 'Unknown', coordinates: [], radius: 0 };
  }
};

/**
 * Get Available Supervisors API
 * Fetches all available supervisors for site assignment
 * Endpoint: /user/supervisors
 * Matches the Flutter API implementation
 */
export const getAvailableSupervisors = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/user/supervisors`);

    const response = await fetch(`${API_BASE_URL}/user/supervisors`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/user/supervisors';
      error.responseData = responseText;

      // Log API error to logging service
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API error: ${response.status} ${response.statusText} for getAvailableSupervisors`,
        error,
        {
          endpoint: '/user/supervisors',
          method: 'GET',
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });

      throw error;
    }

    const result = await response.json();
    console.log('Supervisors fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching supervisors:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_site_onboarding',
        `Site Onboarding API network/fetch error for getAvailableSupervisors`,
        error,
        {
          endpoint: '/user/supervisors',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[siteApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

export default {
  getAllSites,
  getSiteDetails,
  createSite,
  updateSite,
  deleteSite,
  updateGeofence,
  presignSiteLogoUpload,
  uploadSiteLogo,
  getSiteLogoUrl,
  siteHasLogo,
  fetchSiteLogoViewUrl,
  resolveSiteLogoUrl,
  getSitesForCurrentUser,
  getAvailableSupervisors,
  validateSiteData,
  parseGeofenceDetails,
};
